import { useState } from 'react'
import { useAuth } from '../../context/AuthContext'
import { BanknoteCheckIcon, PencilIcon, PhHandCoinsIcon, PlusIcon, PrinterIcon, XIcon } from '../icons'
import { amountInput, applyDiscount, formatTRY } from '../../utils/money'

interface Props {
  totalKurus: number
  /** Rates offered here, in percent. Configured per yurt. */
  discounts: number[]
  onConfirm: (percent: number, print: boolean) => void
  onCancel: () => void
  /**
   * Saves a changed set of rates, when the person at the till is allowed to
   * change them. Left undefined for anyone who is not, a cashier applies a
   * rate, an owner decides what the rates are, same split as menu prices.
   */
  onSaveRates?: (next: number[]) => Promise<void>
  /**
   * Üstü Kalsın (20 Eki 2026): indirim küsuratlı tutar çıkarınca müşteri düz
   * para verip farkı bırakıyor. Verilirse onay adımında üçüncü seçenek.
   */
  onTip?: (percent: number) => void
}

/**
 * The rate-picking half of discounted payment, extracted out of
 * OtherPaymentSheet so SplitPaymentSheet's right-hand panel can offer the
 * same two-step flow (pick a rate, then say what happens to the paper)
 * without a second copy of this logic living beside it.
 *
 * Renders only the inner `.dlg__card` content, not the `.dlg` overlay
 * itself: OtherPaymentSheet owns its own full-screen overlay, and
 * SplitPaymentSheet nests this inside its own right panel rather than a
 * second modal on top of the first.
 */
export function DiscountPicker({
  totalKurus,
  discounts,
  onConfirm,
  onCancel,
  onSaveRates,
  onTip,
}: Props) {
  const { usePrinter } = useAuth()
  // Kept as a string, not a number: an empty box and a typed "0" are different
  // states, and parsing on every keystroke would fight the cashier mid-entry.
  const [custom, setCustom] = useState('')
  /** The rate chosen, once one has been. Null while still choosing. */
  const [picked, setPicked] = useState<number | null>(null)

  /** Set while a rate is being added or removed, to stop a double press. */
  const [savingRates, setSavingRates] = useState(false)

  /**
   * The new-rate box, open only after the + is pressed.
   *
   * Its own field rather than reading the free-entry box at the foot of the
   * sheet: those are two different intentions, one keeps a rate for next
   * time, the other discounts this sale alone, and wiring + to a box the
   * cashier had not typed in yet left the button permanently dimmed with
   * nothing on screen to say why.
   */
  const [adding, setAdding] = useState(false)
  const [addValue, setAddValue] = useState('')

  /**
   * The rate the pencil opened, while it is being changed. Null when the box
   * is adding a new rate rather than replacing one.
   *
   * Editing is a remove and an add in one step: rates are held as plain
   * numbers, so a rate has no identity apart from its own value and "change
   * 20 to 25" can only mean dropping 20 and putting 25 in its place.
   */
  const [editing, setEditing] = useState<number | null>(null)

  const addParsed = Number(addValue.replace(',', '.'))
  const addValid =
    addValue.trim() !== '' &&
    Number.isFinite(addParsed) &&
    addParsed > 0 &&
    addParsed <= 100 &&
    // Unchanged is allowed while editing: it is the rate's own current value,
    // and refusing it would leave Kaydet dead the moment the box opens.
    (addParsed === editing || !discounts.includes(addParsed))

  function closeBox() {
    setAddValue('')
    setAdding(false)
    setEditing(null)
  }

  function openEdit(percent: number) {
    setEditing(percent)
    setAddValue(String(percent))
    setAdding(true)
  }

  async function confirmAdd() {
    if (!addValid || !onSaveRates) return
    const without = editing === null ? discounts : discounts.filter((d) => d !== editing)
    if (!without.includes(addParsed)) {
      setSavingRates(true)
      try {
        await onSaveRates([...without, addParsed].sort((a, b) => a - b))
      } finally {
        setSavingRates(false)
      }
    }
    closeBox()
  }

  async function addRate(percent: number) {
    if (!onSaveRates || discounts.includes(percent)) return
    setSavingRates(true)
    try {
      await onSaveRates([...discounts, percent].sort((a, b) => a - b))
    } finally {
      setSavingRates(false)
    }
  }

  async function removeRate(percent: number) {
    if (!onSaveRates) return
    setSavingRates(true)
    try {
      await onSaveRates(discounts.filter((d) => d !== percent))
    } finally {
      setSavingRates(false)
    }
  }

  // Comma is what the numeric keypad gives on a Turkish layout.
  const parsed = Number(custom.replace(',', '.'))
  const customValid = custom.trim() !== '' && Number.isFinite(parsed) && parsed > 0 && parsed <= 100
  const customResult = customValid ? applyDiscount(totalKurus, parsed) : null

  if (picked !== null) {
    const d = applyDiscount(totalKurus, picked)
    return (
      <div className="dlg__card dconf">
        {/* Sade: tek hesap özeti, bir ana bir ikincil düğme. Önceki halinde
            dört eşit kutu ve iki renkli rakam kutusu yan yanaydı; göz nereye
            bakacağını bilemiyordu (kullanıcı, 2 Eki 2026). */}
        <div className="dconf__head">
          <h2>İndirim %{picked}</h2>
          <button type="button" className="dconf__link" onClick={() => setPicked(null)}>
            Oranı değiştir
          </button>
        </div>

        <dl className="dconf__sum">
          <div>
            <dt>Tutar</dt>
            <dd>{formatTRY(totalKurus)}</dd>
          </div>
          <div>
            <dt>İndirim</dt>
            <dd>−{formatTRY(d.discountKurus)}</dd>
          </div>
          <div className="dconf__pay">
            <dt>Tahsil edilecek</dt>
            <dd>{formatTRY(d.chargedKurus)}</dd>
          </div>
        </dl>

        {/* Kasadaki sırayla aynı: yeşil olan sağda. */}
        <div className={`dconf__actions ${usePrinter ? '' : 'dconf__actions--one'}`}>
          <button
            type="button"
            className={`dconf__btn ${usePrinter ? '' : 'dconf__btn--main'}`}
            onClick={() => onConfirm(picked, false)}
          >
            <BanknoteCheckIcon />
            Öde &amp; Kapat
          </button>
          {usePrinter && (
          <button
            type="button"
            className="dconf__btn dconf__btn--main"
            onClick={() => onConfirm(picked, true)}
          >
            <PrinterIcon />
            Öde, Yazdır &amp; Kapat
          </button>
          )}
        </div>

        {onTip && (
          <button type="button" className="dconf__tip" onClick={() => onTip(picked)}>
            <PhHandCoinsIcon />
            Üstü Kalsın
          </button>
        )}

        <button type="button" className="dconf__cancel" onClick={onCancel}>
          Vazgeç
        </button>
      </div>
    )
  }

  return (
    <div className="dlg__card">
      <h2>İndirimli Ödeme</h2>

      <div className="dlg__total">
        <span>Tutar</span>
        <strong>{formatTRY(totalKurus)}</strong>
      </div>

      {/* Above the cards and outside .discs, so it stays exactly where it is
          once the last rate is removed, otherwise emptying the list would
          take away the only way to refill it. Hidden from a cashier, who has
          no rates to add and would only see a dead control. */}
      {onSaveRates && (
        <div className="discs__head">
          <span className="discs__head-label">
            {editing === null ? 'Yeni indirim' : `İndirim %${editing}`}
          </span>
          {!adding && (
            <button
              type="button"
              className="discs__add"
              onClick={() => setAdding(true)}
              title="Yeni indirim oranı ekle"
              aria-label="Yeni indirim oranı ekle"
            >
              <PlusIcon />
            </button>
          )}
        </div>
      )}

      {onSaveRates && adding && (
        <div className="discs__new">
          <div className="discs__new-field">
            <input
              className="discs__new-input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              value={addValue}
              onChange={(e) => setAddValue(amountInput(e.target.value, true))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void confirmAdd()
                }
                if (e.key === 'Escape') {
                  e.preventDefault()
                  closeBox()
                }
              }}
            />
            <span className="discs__new-pct">%</span>
          </div>
          <button
            type="button"
            className="btn btn--primary discs__new-save"
            disabled={!addValid || savingRates}
            onClick={() => void confirmAdd()}
          >
            {savingRates ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
          <button
            type="button"
            className="btn btn--ghost discs__new-cancel"
            onClick={closeBox}
          >
            Vazgeç
          </button>
        </div>
      )}

      {discounts.length === 0 && !adding && (
        <p className="discs__empty">
          {onSaveRates
            ? 'Hazır oran yok. Yukarıdaki + ile bir oran ekleyin.'
            : 'Hazır oran yok. Aşağıya oranı yazıp uygulayabilirsiniz.'}
        </p>
      )}

      <div className="discs">
        {discounts.map((percent) => {
          const d = applyDiscount(totalKurus, percent)
          return (
            <div key={percent} className="discs__slot">
              <button
                type="button"
                className="discs__card"
                onClick={() => setPicked(percent)}
              >
                <span className="discs__rate">İndirim %{percent}</span>
                <span className="discs__off">−{formatTRY(d.discountKurus)}</span>
                <span className="discs__pay">{formatTRY(d.chargedKurus)}</span>
              </button>

              {/* Inside the card rather than alongside it, stacked in their own
                  column. Only the owner sees them: a cashier applies a rate, an
                  owner decides which rates exist, the same split as menu
                  prices.

                  Siblings of .discs__card, not children of it: a button cannot
                  contain another button, and the card itself is the button that
                  picks the rate. The card's own padding is what leaves this
                  column its room. */}
              {onSaveRates && (
                <div className="discs__tools">
                  <button
                    type="button"
                    className="discs__tool discs__tool--drop"
                    disabled={savingRates}
                    onClick={() => removeRate(percent)}
                    title={`%${percent} oranını kaldır`}
                    aria-label={`%${percent} oranını kaldır`}
                  >
                    <XIcon />
                  </button>
                  <button
                    type="button"
                    className="discs__tool"
                    disabled={savingRates}
                    onClick={() => openEdit(percent)}
                    title={`%${percent} oranını değiştir`}
                    aria-label={`%${percent} oranını değiştir`}
                  >
                    <PencilIcon />
                  </button>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {/* Below the ready rates, not among them: the common case stays one tap,
          and the odd rate is there when the till needs it. */}
      <div className="discx">
        <label className="discx__label" htmlFor="discx-input">
          Farklı indirim oranı
        </label>
        <div className="discx__row">
          <div className="discx__field">
            <input
              id="discx-input"
              className="discx__input"
              type="text"
              inputMode="decimal"
              autoComplete="off"
              placeholder="0"
              value={custom}
              onChange={(e) => setCustom(amountInput(e.target.value, true))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && customValid) {
                  e.preventDefault()
                  setPicked(parsed)
                }
              }}
            />
            <span className="discx__pct">%</span>
          </div>
          <button
            type="button"
            className="btn btn--primary discx__apply"
            disabled={!customValid}
            onClick={() => customValid && setPicked(parsed)}
          >
            Uygula
          </button>
          {/* On the same line as the rate it would abandon: a rate box only
              needs room for two digits, and stranding Vazgeç on a row of its
              own left the sheet with an empty band across the bottom. Saving
              a rate used to be a third button here, which is what pushed this
              one off the edge of the sheet, it is the + on the header row
              now. */}
          <button type="button" className="btn btn--ghost discx__cancel" onClick={onCancel}>
            Vazgeç
          </button>
        </div>

        {/* The same two figures the cards show, so the free rate is read the
            same way as a preset one. */}
        {customResult && (
          <div className="discx__preview">
            <span className="discx__off">−{formatTRY(customResult.discountKurus)}</span>
            <span className="discx__pay">{formatTRY(customResult.chargedKurus)}</span>
          </div>
        )}
      </div>
    </div>
  )
}
